import { NormalizedLoad } from 'types/Load';

import {
  MGBFscRateQualifier,
  MGB_DEFAULT_FSC_RATE_QUALIFIER,
  MGB_DEFAULT_RATE_MODE,
  MGB_FSC_RATE_QUALIFIER,
} from '../constants';
import { resolveMGBStopCountryForSubmit } from '../mgbGeography';
import { ensureMGBRouteStops } from '../mgbStops';
import { MGBLineHaulUnit } from '../types';

// "Flat" is a rate type, not a transport Mode (ENG-7739). Flatbed is valid.
export function isMGBExcludedMode(value: unknown): boolean {
  return typeof value === 'string' && value.trim().toLowerCase() === 'flat';
}

// Mode for MG ImportWeb ReferenceNumber — Modes code table only; never Flat.
export function normalizeMGBRateMode(value: unknown): string {
  if (typeof value !== 'string') {
    return MGB_DEFAULT_RATE_MODE;
  }
  const trimmed = value.trim();
  if (!trimmed || isMGBExcludedMode(trimmed)) {
    return MGB_DEFAULT_RATE_MODE;
  }
  return trimmed;
}

/** Parse RHF number input; empty string and invalid values → null. */
export function parseOptionalFormNumber(value: unknown): number | null {
  if (value == null || value === '') {
    return null;
  }
  const n = Number(value);

  return Number.isNaN(n) ? null : n;
}

function positiveFormNumber(value: unknown): number | null {
  const n = parseOptionalFormNumber(value);
  if (n == null || n <= 0) {
    return null;
  }
  return n;
}

/**
 * Infer FSC qualifier from which rate fields are populated (PM > FR > PCT).
 * Used to initialize the Rates dropdown when loading an existing load.
 */
export function deriveMGBFscRateQualifier(
  rateData:
    | Partial<
        Pick<
          NormalizedLoad['rateData'],
          'fscPercent' | 'fscFlatRate' | 'fscPerMile'
        >
      >
    | null
    | undefined
): MGBFscRateQualifier {
  if (positiveFormNumber(rateData?.fscPerMile) != null) {
    return MGB_FSC_RATE_QUALIFIER.PerMile;
  }

  if (
    positiveFormNumber(rateData?.fscFlatRate) != null &&
    positiveFormNumber(rateData?.fscPercent) == null
  ) {
    return MGB_FSC_RATE_QUALIFIER.FlatRate;
  }

  if (positiveFormNumber(rateData?.fscPercent) != null) {
    return MGB_FSC_RATE_QUALIFIER.Percent;
  }

  return MGB_DEFAULT_FSC_RATE_QUALIFIER;
}

/** Clear FSC fields that do not apply to the selected qualifier. */
export function clearMGBFscFieldsForQualifier(
  setValue: (
    name: string,
    value: number | null,
    options?: { shouldDirty?: boolean }
  ) => void,
  qualifier: MGBFscRateQualifier
): void {
  const opts = { shouldDirty: true };
  if (qualifier !== MGB_FSC_RATE_QUALIFIER.Percent) {
    setValue('rateData.fscPercent', null, opts);
  }

  if (qualifier !== MGB_FSC_RATE_QUALIFIER.FlatRate) {
    setValue('rateData.fscFlatRate', null, opts);
  }

  if (qualifier !== MGB_FSC_RATE_QUALIFIER.PerMile) {
    setValue('rateData.fscPerMile', null, opts);
    // Keep rateData.miles — shared with Rate Per Mile / Flash lookup (ENG-7727).
  }
}

/** Computed Fuel Surcharge total for Rates UI display. */
export function computeMGBFscTotal(args: {
  qualifier: MGBFscRateQualifier;
  lineHaul: unknown;
  fscPercent: unknown;
  fscFlatRate: unknown;
  fscPerMile: unknown;
  miles: unknown;
}): number | null {
  const { qualifier } = args;
  if (qualifier === MGB_FSC_RATE_QUALIFIER.Percent) {
    const lineHaul = positiveFormNumber(args.lineHaul);
    const pct = positiveFormNumber(args.fscPercent);
    if (lineHaul == null || pct == null) {
      return null;
    }
    return (lineHaul * pct) / 100;
  }

  if (qualifier === MGB_FSC_RATE_QUALIFIER.FlatRate) {
    return positiveFormNumber(args.fscFlatRate);
  }

  const rate = positiveFormNumber(args.fscPerMile);
  const miles = positiveFormNumber(args.miles);
  if (rate == null || miles == null) {
    return null;
  }

  return rate * miles;
}

/**
 * Line haul + fuel surcharge + customer line items.
 * Matches MG price-sheet Total so post-update Diff does not flag Customer Total Charge.
 */
export function computeMGBCustomerTotalCharge(
  rateData:
    | {
        customerLineHaulCharge?: { val?: unknown } | null;
        fscPercent?: unknown;
        fscFlatRate?: unknown;
        fscPerMile?: unknown;
        miles?: unknown;
        customerLineItems?: Array<{ totalChargeUSD?: unknown }> | null;
      }
    | null
    | undefined
): number {
  const lineHaul = parseOptionalFormNumber(
    rateData?.customerLineHaulCharge?.val
  );
  const qualifier = deriveMGBFscRateQualifier({
    fscPercent: parseOptionalFormNumber(rateData?.fscPercent),
    fscFlatRate: parseOptionalFormNumber(rateData?.fscFlatRate),
    fscPerMile: parseOptionalFormNumber(rateData?.fscPerMile),
  });
  const fsc =
    computeMGBFscTotal({
      qualifier,
      lineHaul: rateData?.customerLineHaulCharge?.val,
      fscPercent: rateData?.fscPercent,
      fscFlatRate: rateData?.fscFlatRate,
      fscPerMile: rateData?.fscPerMile,
      miles: rateData?.miles,
    }) ?? 0;
  const lineItemsTotal = (rateData?.customerLineItems ?? []).reduce(
    (sum, item) => {
      const amount = parseOptionalFormNumber(item.totalChargeUSD);
      return sum + (amount != null && amount > 0 ? amount : 0);
    },
    0
  );

  return (
    (lineHaul != null && lineHaul > 0 ? lineHaul : 0) + fsc + lineItemsTotal
  );
}

/** Rate Per Mile mode when line-haul unit is PM (or alias) (ENG-7727). */
export function isMGBRatePerMileMode(unit: string | null | undefined): boolean {
  const u = (unit ?? '').trim().toUpperCase();
  return (
    u === MGBLineHaulUnit.PerMile ||
    u === MGBLineHaulUnit.PerMileLabel ||
    u === MGBLineHaulUnit.Mileage
  );
}

/** Line haul total = miles × rate per mile when both positive. */
export function computeMGBLineHaulFromPerMile(
  miles: unknown,
  ratePerMile: unknown
): number | null {
  const m = positiveFormNumber(miles);
  const r = positiveFormNumber(ratePerMile);
  if (m == null || r == null) {
    return null;
  }
  return Math.round(m * r * 100) / 100;
}

/** MGB stop address fields used for Kenco Flash mileage (ENG-7727). */
type MGBMileageStopLocation =
  | {
      zipCode?: string | null;
      city?: string | null;
      state?: string | null;
      country?: string | null;
      /** Legacy nested shape if present (e.g. other TMS). */
      address?: {
        zip?: string | null;
        zipCode?: string | null;
        city?: string | null;
        state?: string | null;
        country?: string | null;
      } | null;
    }
  | null
  | undefined;

function mgbStopZip(stop: MGBMileageStopLocation): string {
  return (
    stop?.zipCode?.trim() ||
    stop?.address?.zipCode?.trim() ||
    stop?.address?.zip?.trim() ||
    ''
  );
}

function mgbStopCity(stop: MGBMileageStopLocation): string {
  return stop?.city?.trim() || stop?.address?.city?.trim() || '';
}

function mgbStopState(stop: MGBMileageStopLocation): string {
  return stop?.state?.trim() || stop?.address?.state?.trim() || '';
}

function mgbStopCountry(stop: MGBMileageStopLocation): string {
  return stop?.country?.trim() || stop?.address?.country?.trim() || '';
}

/** True when a stop has enough address data for Flash mileage (zip or city+state). */
export function mgbStopHasMileageLocation(
  stop: MGBMileageStopLocation
): boolean {
  if (mgbStopZip(stop)) {
    return true;
  }
  return Boolean(mgbStopCity(stop) && mgbStopState(stop));
}

export function mgbStopToFlashMileageStop(stop: MGBMileageStopLocation): {
  zip?: string;
  city?: string;
  state?: string;
  country?: string;
} {
  const state = mgbStopState(stop);
  const country = resolveMGBStopCountryForSubmit(mgbStopCountry(stop), state);
  return {
    zip: mgbStopZip(stop) || undefined,
    city: mgbStopCity(stop) || undefined,
    state: state || undefined,
    country,
  };
}

/** True when every route stop has enough address data for Flash mileage. */
export function mgbRouteStopsReadyForFlashMileage(
  values: Pick<NormalizedLoad, 'stops' | 'pickup' | 'consignee'>
): boolean {
  const routeStops = ensureMGBRouteStops(values as NormalizedLoad);
  if (routeStops.length < 2) {
    return false;
  }
  return routeStops.every((stop) => mgbStopHasMileageLocation(stop));
}

/** Stable lane key for the full ordered route (origin → intermediates → destination). */
export function flashMileageLaneKeyFromRouteStops(
  values: Pick<NormalizedLoad, 'stops' | 'pickup' | 'consignee'>,
  equipmentType: string
): string {
  const routeStops = ensureMGBRouteStops(values as NormalizedLoad);
  const stopKey = (s: ReturnType<typeof mgbStopToFlashMileageStop>) =>
    [s.zip, s.city, s.state, s.country]
      .map((part) => (part ?? '').trim().toUpperCase())
      .join('|');
  const path = routeStops
    .map((stop) => stopKey(mgbStopToFlashMileageStop(stop)))
    .join('>');
  return `${path}|${equipmentType.trim()}`;
}
